import {
  Toast,
  ToastBody,
  ToastTitle,
  useToastController,
  Link,
} from "@fluentui/react-components";
import { Toaster } from "@fluentui/react-toast";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useId,
} from "react";

const STORAGE_KEY = "listingPageState";

export interface DateRange {
  start?: string;
  end?: string;
}

export interface FilterState {
  archived?: boolean;
  assignedTo?: string;
  customer_segment?: string[];
  funding_type?: string[];
  maxAmount?: string; // Funding Amount
  maxLakeshoreAmount?: string; // Lakeshore Amount
  minAmount?: string; // Funding Amount
  minLakeshoreAmount?: string; // Lakeshore Amount
  priority_tier?: string[];
  region?: string[];
  search_query?: string;
  sent_to_salesforce?: boolean;
  ai_junk?: boolean; // "Junk (AI)": sent to the BFF as ai_junk=true|false
  source_name?: string[];
  stage?: string;
  state?: string[];
  status?: string[];
  tier?: string[];
  topic?: string[];
  validated?: boolean;
  watchlist?: number;
  tagged?: number | string; // User ID for tagged items or special values like "any" or "none"
  // Date filters
  article_date?: DateRange;
  application_deadline?: DateRange;
  award_date?: DateRange;
  spending_deadline?: DateRange;
  funding_opp_created_at?: DateRange;
  funding_opp_updated_at?: DateRange;
  projected_timeline?: DateRange;
  spend_down?: DateRange;
  funding_home?: boolean;
  recent_updates?: boolean;
  // Special property to track deleted properties
  __deleted_properties?: string[];
}

interface SortState {
  sortColumn: string;
  sortDirection: "ascending" | "descending";
}

interface ListingPageState {
  filters: FilterState;
  sortState: SortState;
  currentPage: number;
}

interface FilterContextType {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  sortState: SortState;
  setSortState: (state: SortState) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  clearFilters: () => void;
  deleteFilters: (remove: FilterState) => void;
  addFilters: (add: FilterState) => void;
  tempFilters: FilterState;
  setTempFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  applyTempFilters: () => void;
  clearTempFilters: () => void;
  registerDataRefresh: (refreshFn: () => Promise<void>) => void;
}

const defaultSortState: SortState = {
  sortColumn: "name",
  sortDirection: "ascending",
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<ListingPageState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsedState = saved ? JSON.parse(saved) : null;

    // Ensure we have default values for all required properties
    return {
      filters: parsedState?.filters || {},
      sortState: parsedState?.sortState || defaultSortState,
      currentPage: parsedState?.currentPage || 1,
    };
  });
  const [filters, setFilters] = useState(state.filters);
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [sortState, setSortStateInternal] = useState(state.sortState);
  const [currentPage, setCurrentPageInternal] = useState(state.currentPage);
  const toasterId = useId();
  const { dispatchToast } = useToastController(toasterId);

  // Add a ref to store the dataRefresh function
  const dataRefreshRef = React.useRef<(() => Promise<void>) | null>(null);
  
  // Function to register the dataRefresh function
  const registerDataRefresh = (refreshFn: () => Promise<void>) => {
    dataRefreshRef.current = refreshFn;
  };
  
  // Apply temporary filters to the actual filters
  const applyTempFilters = () => {
    setFilters(current => {
      // Create a new filters object
      const newFilters = { ...current };
      
      // Process each key in tempFilters
      Object.keys(tempFilters).forEach(key => {
        const typedKey = key as keyof FilterState;
        const value = tempFilters[typedKey];
        
        // If the value is undefined, remove the property
        if (value === undefined) {
          delete newFilters[typedKey];
        } 
        // If the value is an empty array, explicitly remove the property
        else if (Array.isArray(value) && value.length === 0) {
          delete newFilters[typedKey];
        } 
        // Otherwise, update with the new value
        else {
          // Otherwise, update with the new value
          newFilters[typedKey] = value as any;
        }
      });
      
      // Check for properties that need to be explicitly removed
      // This handles the case where a property was deleted from tempFilters
      // We need to look for special markers in tempFilters that indicate deletion
      if ('__deleted_properties' in tempFilters && Array.isArray(tempFilters.__deleted_properties)) {
        (tempFilters.__deleted_properties as string[]).forEach(prop => {
          delete newFilters[prop as keyof FilterState];
        });
      }
      
      return newFilters;
    });
    setTempFilters({});
    // New filters mean a new result set; the saved page may be out of range
    setCurrentPageInternal(1);
  };

  // Clear temporary filters
  const clearTempFilters = () => {
    setTempFilters({});
  };

  const notifyAdd = (type: string, value: string) => {
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <Link
              onClick={() => {
                deleteFilters({ [type]: [value] });
              }}
            >
              Undo
            </Link>
          }
        >
          Filter applied
        </ToastTitle>
        <ToastBody subtitle="Subtitle">
          {type}: {value}
        </ToastBody>
      </Toast>,
      { intent: "success", timeout: 3000 }
    );
  };

  const notifyRemove = (type: string, value: string) => {
    dispatchToast(
      <Toast>
        <ToastTitle>Filter removed</ToastTitle>
        <ToastBody>
          {type}: {value}
        </ToastBody>
      </Toast>,
      { intent: "success", timeout: 3000 }
    );
  };

  useEffect(() => {
    const newState = { filters, sortState, currentPage };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  }, [filters, sortState, currentPage]);

  const setSortState = (newSortState: SortState) => {
    setSortStateInternal(newSortState);
  };
  
  const setCurrentPage = (newPage: number) => {
    setCurrentPageInternal(newPage);
  };

  // Enhanced filter operations
  const addFiltersWithRefresh = (add: FilterState) => {
    // We'll trigger the refresh after applying all filter changes
    addFilters(add);
    
    // Call dataRefresh if available (only once after all filter changes)
    if (dataRefreshRef.current) {
      dataRefreshRef.current();
    }
  };
  
  const deleteFiltersWithRefresh = (remove: FilterState) => {
    // We'll trigger the refresh after applying all filter changes
    deleteFilters(remove);
    
    // Call dataRefresh if available (only once after all filter changes)
    if (dataRefreshRef.current) {
      dataRefreshRef.current();
    }
  };
  
  const clearFiltersWithRefresh = () => {
    // We'll trigger the refresh after applying all filter changes
    clearFilters();
    
    // Call dataRefresh if available (only once after all filter changes)
    if (dataRefreshRef.current) {
      dataRefreshRef.current();
    }
  };

  const addFilters = (add: FilterState) => {
    setFilters((prev) => {
      const newFilters = { ...prev };

      // Fast synchronous update for UI
      Object.entries(add).forEach(([key, value]) => {
        if (value === undefined) {
          delete newFilters[key as keyof FilterState];
          return;
        }

        if (Array.isArray(value)) {
          if (value.length === 0) {
            delete newFilters[key as keyof FilterState];
          } else {
            (newFilters[key as keyof FilterState] as any) = value;
          }
        } else if (typeof value === 'object' && value !== null && 'start' in value) {
          // Handle DateRange objects
          const dateRange = value as DateRange;
          if (!dateRange.start && !dateRange.end) {
            delete newFilters[key as keyof FilterState];
          } else {
            (newFilters[key as keyof FilterState] as any) = value;
          }
        } else {
          (newFilters[key as keyof FilterState] as any) = value;
        }
      });
      return newFilters;
    });
    setCurrentPageInternal(1);
  };

  const deleteFilters = (remove: FilterState) => {
    const newFilters = { ...filters };
    Object.keys(remove).forEach((filter) => {
      const key = filter as keyof FilterState;
      const value = remove[key];

      if (value === undefined) {
        delete newFilters[key];
        return;
      }

      switch (key) {
        case "region":
        case "state":
        case "status":
        case "customer_segment":
        case "tier":
        case "topic":
        case "funding_type":
        case "priority_tier":
        case "source_name": {
          if (Array.isArray(value)) {
            if (newFilters[key] && Array.isArray(newFilters[key])) {
              (newFilters[key] as string[]) = (newFilters[key] as string[]).filter(
                (item) => !value.includes(item)
              );
              if ((newFilters[key] as string[]).length === 0) {
                delete newFilters[key];
              }
            }
            notifyRemove(key, value.join(", "));
          }
          break;
        }
        case "article_date":
        case "application_deadline":
        case "award_date":
        case "spending_deadline":
        case "funding_opp_created_at":
        case "funding_opp_updated_at":
        case "projected_timeline":
        case "spend_down": {
          delete newFilters[key];
          const dateRange = value as DateRange;
          const rangeStr = dateRange.start && dateRange.end 
            ? `${dateRange.start} to ${dateRange.end}`
            : dateRange.start || dateRange.end || "";
          notifyRemove(key, rangeStr);
          break;
        }
        case "sent_to_salesforce":
          delete newFilters.sent_to_salesforce;
          notifyRemove("sent_to_salesforce", value ? "Yes" : "No");
          break;
        case "ai_junk":
          delete newFilters.ai_junk;
          notifyRemove("ai_junk", value ? "Junk (AI)" : "Not Junk (AI)");
          break;
        case "validated":
          delete newFilters.validated;
          notifyRemove("validated", value ? "Validated" : "Not Validated");
          break;
        case "stage":
          delete newFilters[key];
          notifyRemove(key, String(value));
          break;
        case "minAmount":
        case "maxAmount":
        case "minLakeshoreAmount":
        case "maxLakeshoreAmount":
          delete newFilters[key];
          notifyRemove(key, String(value));
          break;
        case "search_query":
          delete newFilters.search_query;
          break;
        default:
          if (value !== undefined) {
            delete newFilters[key];
            notifyRemove(key, String(value));
          }
          break;
      }
    });
    setFilters(newFilters);
    setCurrentPageInternal(1);
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPageInternal(1);
  };

  return (
    <>
      <FilterContext.Provider
        value={{
          filters,
          setFilters,
          sortState,
          setSortState,
          currentPage,
          setCurrentPage,
          clearFilters: clearFiltersWithRefresh,
          deleteFilters: deleteFiltersWithRefresh,
          addFilters: addFiltersWithRefresh,
          tempFilters,
          setTempFilters,
          applyTempFilters,
          clearTempFilters,
          registerDataRefresh,
        }}
      >
        {children}
      </FilterContext.Provider>
      <Toaster toasterId={toasterId} />
    </>
  );
};

export const useFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error("useFilters must be used within a FilterProvider");
  }
  return context;
};
